<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div class="layout--main" :class="[layoutTypeClass, navbarClasses, footerClasses, {'no-scroll': isAppPage}]">

    <div id="content-area">
      <div id="content-overlay"/>

      <div class="h-full flex">
        <amio-sidebar/>
        <div class="content-wrapper-x">

          <div class="router-content h-full">

            <div class="content-area__content h-full">

              <transition :name="routerTransition" mode="out-in">
                <router-view @changeRouteTitle="changeRouteTitle"
                             @setAppClasses="(classesStr) => $emit('setAppClasses', classesStr)"/>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeConfig from '@/../themeConfig.js'
import AmioSidebar from '@/components/AmioSidebar'
import { theme } from '../../../tailwind.config'
import botModule from '@/store/modules/bot.module'
import channelModule from '@/store/modules/channel.module'
import multiBotService from '@/services/amio/multi-bot.service'
import { PATHS, ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { editorBotIdFromRoute, resolveLastOpenedBot } from '@/utils/last-opened-bot'
import isEmpty from 'ramda/src/isEmpty'
import UserProfileService from '@/services/amio/user-profile.service'

export default {
  components: {
    AmioSidebar
  },
  data() {
    return {
      footerType: themeConfig.footerType || 'static',
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || '#fff',
      navbarType: themeConfig.navbarType || 'floating',
      routerTransition: themeConfig.routerTransition || 'none',
      routeTitle: this.$route.meta.pageTitle,
      upIconSize: theme.fontSize['3xl']
    }
  },
  watch: {
    '$route'() {
      this.routeTitle = this.$route.meta.pageTitle
    },
    isThemeDark(val) {
      const color = this.navbarColor == '#fff' && val ? '#10163a' : '#fff'
      this.updateNavbarColor(color)
    },
    '$store.state.mainLayoutType'(val) {
      this.setNavMenuVisibility(val)
    }
  },
  computed: {
    bots: botModule.getBotList,

    bodyOverlay() { return this.$store.state.bodyOverlay },
    contentAreaClass() {
      if (this.mainLayoutType === 'vertical') {
        if (this.verticalNavMenuWidth == 'default') return 'content-area-reduced'
        else if (this.verticalNavMenuWidth == 'reduced') return 'content-area-lg'
        return 'content-area-full'
      }
      // else if(this.mainLayoutType === "boxed") return "content-area-reduced"
      return 'content-area-full'
    },
    footerClasses() {
      return {
        'footer-hidden': this.footerType == 'hidden',
        'footer-sticky': this.footerType == 'sticky',
        'footer-static': this.footerType == 'static'
      }
    },
    isAppPage() {
      return this.$route.meta.no_scroll
    },
    isThemeDark() { return this.$store.state.theme == 'dark' },
    layoutTypeClass() { return `main-${this.mainLayoutType}` },
    mainLayoutType() { return this.$store.state.mainLayoutType },
    navbarClasses() {
      return {
        'navbar-hidden': this.navbarType == 'hidden',
        'navbar-sticky': this.navbarType == 'sticky',
        'navbar-static': this.navbarType == 'static',
        'navbar-floating': this.navbarType == 'floating'
      }
    },

    username() {
      return UserProfileService.getEmail()
    },

    verticalNavMenuWidth() { return this.$store.state.verticalNavMenuWidth },
    windowWidth() { return this.$store.state.windowWidth }
  },
  methods: {
    changeRouteTitle(title) {
      this.routeTitle = title
    },
    updateNavbarColor(val) {
      this.navbarColor = val
      if (val == '#fff') this.isNavbarDark = false
      else this.isNavbarDark = true
    },
    setNavMenuVisibility(layoutType) {
      if ((layoutType === 'horizontal' && this.windowWidth >= 1200) || (layoutType === 'vertical' && this.windowWidth < 1200)) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
        this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')
      } else {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
      }
    },

    async initEngineData() {
      botModule.setBotLoading(true)
      await botModule.setBotList()

      const botVariantList = await multiBotService.list()
      await botModule.setBotVariantList(botVariantList)

      if (isEmpty(this.bots)) {
        botModule.setBot({ id: ITEM_IDS.NEW })
        botModule.setBotLoading(false)
        if (this.$route.path.startsWith(PATHS.settings)) return

        this.delayedRouterReplace({ name: ROUTE_NAMES.bots, params: { botId: ITEM_IDS.NEW } })
        return
      }

      const editorBotId = editorBotIdFromRoute(this.$route)
      const routeBot = editorBotId ? this.bots.find(b => b.id === editorBotId) : null
      // A direct editor link wins. Settings and other routes restore the last opened variant,
      // then pin the store to that variant's primary. The route id is what reopens the market.
      const anchorBot = routeBot || resolveLastOpenedBot(this.bots)
      const primaryBot = botModule.resolvePrimaryBot(anchorBot && anchorBot.id) || this.bots[0]
      botModule.setBot(primaryBot)
      botModule.setBotLoading(false)
      if (editorBotId && !routeBot) {
        this.delayedRouterReplace({
          name: this.$route.name,
          params: { ...this.$route.params, botId: primaryBot.id },
          query: this.$route.query
        })
      }
      channelModule.setChannelList(primaryBot.id)
    },

    // in router.js - Main.vue and children components are loaded in parallel
    // if invalid botId is used in children, then the redirect to 404 page overrides main.vue router.push()
    delayedRouterReplace(location) {
      setTimeout(() => {
        this.$router.replace(location).catch(() => {})
      }, 10)
    }
  },

  created() {
    const color = this.navbarColor == '#fff' && this.isThemeDark ? '#10163a' : this.navbarColor
    this.updateNavbarColor(color)
    this.setNavMenuVisibility(this.$store.state.mainLayoutType)
  },

  mounted() {
    this.initEngineData()
  }
}

</script>

<style scoped>
.content-wrapper-x {
  padding-left: 100px;
  min-height: 100vh;
  width: 100%;
}
</style>
